import React, { useRef, useState, useEffect } from "react";
import {
  Modal, Button, Form, Alert, Row, Col, Image, Badge,
} from "react-bootstrap";
import { useMutation } from "@apollo/client";
import store from "../../../redux/store";
import { FeedbackImageUploadLink, uploadClient } from "../../../redux/setupApolloClient";
import { FILE_UPLOAD_MUTATION } from "../_Commons/Constants/Mutations/Files";
import {
  AccountBalance as CGIcon,
  LocationCity as LGIcon,
  Domain as SEPCIcon,
} from "@material-ui/icons";

function FeedbackModal(props) {
  const {
    action, readOnly = false, handleSubmit, show, onHide,
    onHideDelete, handleDelete, showDelete,
    Feedback_Category, changeFeedback_Category,
    Rate_Of_Occurence, changeRate_Of_Occurence,
    Feedback_Description, changeFeedback_Description,
    Impact, changeImpact,
    Image: imageFile, changeImage,
    Status, changeStatus,
    Assigned_To, changeAssignedTo,
    Resolution_Summary, changeResolutionSummary,
    Resolution_Type, changeResolutionType,
    loading = false, error = null,
    isSuperAdmin = false, isBPEDMaster = false, isAdmin = false, isBPED = false,
    isReviewer = false,
    detectedUserType = "CG", userRoleDisplay = "Viewer",
    VoteInfo = "", voteDisplayInfo = "",
    creatorVoteDisplay = "",
    creatorRoleDisplay = "",
    creatorEmail = "",
    creatorPhone = "",
    creatorDisplayName = "",
    Ticket_No = "", userName = "",
    assignableUsers = [], usersLoading = false,
    isEditMode = false, editableFields = [],
    lgVoteData = null, cgVoteData = null, sepVoteData = null,
    selectedFeedback = null,
    availableVotes = { CG: [], LG: [], SEPC: [] },
    selectedType = "CG", onTypeChange = () => {},
    selectedVote = null, onVoteChange = () => {},
  } = props;

  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState("");

  const imageRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState("");
  const [client, setClient] = useState(null);

  useEffect(() => setClient(uploadClient(store)), []);
  useEffect(() => setSelectedImage(""), [action]);

  const [uploadFile] = useMutation(FILE_UPLOAD_MUTATION, { client });

  // ==================== OPTIONS ====================
  const Feedback_CategorysOptions = [
    "Log In", "Planning Stage", "Preparation Stage", "Approval Stage",
    "Execution Stage", "Quarterly Reporting", "Reports Generated", "System Improvement", "Other",
  ];

  const statusOptions = [
    { value: "Pending Review", label: "Pending Review" },
    { value: "Assigned", label: "Assigned" },
    { value: "Escalated", label: "Escalated" },
    { value: "Resolved", label: "Resolved" },
  ];

  const ImpactOptions = [
    "Minor (Minor Inconvenience)",
    "Major (Affects work but workaround exists)",
    "Critical (Work blocked, System Down)",
  ];

  const rate_Of_OccurenceOptions = ["First time", "Rarely", "Occasionally", "Frequently"];

  const Resolution_TypeOptions = [
    "Application", "Database", "Deployment", "Reports", "User Creation",
    "Backend", "Frontend", "servers", "Certificates", "others",
  ];

  const isCreateMode = action === "Add";
  const isViewMode = action === "View";
  const isEditModeFromAction = action === "Update";
  const isResolveMode = action === "Resolve";

  const isFieldEditable = (fieldName) => {
    if (readOnly || isViewMode) return false;
    if (isCreateMode) return true;
    if (isEditMode || isEditModeFromAction) return editableFields.includes(fieldName);
    if (isResolveMode) return fieldName === "Resolution_Summary";
    return true;
  };

  const showReviewerFields =
    isReviewer && (isEditMode || isEditModeFromAction || isViewMode || isResolveMode);

  const renderFieldWithLabelOnLeft = (label, field, required = false, helpText = null) => (
    <Form.Group as={Row} className="mb-3 align-items-center">
      <Form.Label column sm={3} className="text-nowrap">
        {label} {required && <span className="text-danger">*</span>}
      </Form.Label>
      <Col sm={9}>
        {field}
        {helpText && <Form.Text className="text-muted">{helpText}</Form.Text>}
      </Col>
    </Form.Group>
  );

  // ==================== IMAGE HANDLING ====================
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("File size must be less than 5MB"); return; }
    if (!file.type.startsWith("image/")) { alert("Please select an image file"); return; }
    try {
      const result = await uploadFile({ variables: { file, directory: "FeedbackImageUpload" } });
      if (result.data?.uploadFile?.File) {
        const fileName = result.data.uploadFile.File;
        setSelectedImage(URL.createObjectURL(file));
        changeImage(fileName);
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload image. Please try again.");
    }
  };

  const handleImageClick = (imageSrc) => {
    setPreviewImageUrl(imageSrc);
    setShowImagePreview(true);
  };

  const getUserTypeInfo = (type) => {
    const typeMap = {
      LG: { name: "Local Government", short: "LG", icon: LGIcon, variant: "success" },
      CG: { name: "Central Government", short: "CG", icon: CGIcon, variant: "warning" },
      SEPC: { name: "SEPC", short: "SEPC", icon: SEPCIcon, variant: "info" },
    };
    return typeMap[type] || typeMap["CG"];
  };

  // ==================== VOTE HELPERS ====================
  const safeJoin = (code, name) => {
    if (code && name) return `${code} - ${name}`;
    return code || name || "";
  };

  const getVoteCode = () => {
    if (isSuperAdmin && isCreateMode && selectedVote) {
      const vote = availableVotes[selectedType]?.find((v) => v.value === selectedVote);
      const codePart = vote?.label?.split(" - ")[0];
      if (codePart) return codePart;
    }
    if (detectedUserType === "LG" && lgVoteData)   return lgVoteData.Vote_Code || "LG000";
    if (detectedUserType === "CG" && cgVoteData)   return cgVoteData.Vote_Code || "CG000";
    if (detectedUserType === "SEPC" && sepVoteData) return sepVoteData.Vote_Code || sepVoteData.Parastatal_Code || "SEPC000";
    return detectedUserType === "LG" ? "LG000" : detectedUserType === "SEPC" ? "SEPC000" : "CG000";
  };

  const getUsername = () => {
    if (userName) return userName.replace(/[^a-zA-Z0-9]/g, "").toLowerCase().substring(0, 10);
    return "user";
  };

  const getVoteDisplayInfo = () => {
    if (!isCreateMode && creatorVoteDisplay) return creatorVoteDisplay;

    if (isSuperAdmin && isCreateMode && selectedVote) {
      const vote = availableVotes[selectedType]?.find((v) => v.value === selectedVote);
      if (vote) return vote.label || "";
    }
    if (isCreateMode && voteDisplayInfo && voteDisplayInfo !== "Not assigned") return voteDisplayInfo;

    if (isCreateMode) {
      if (detectedUserType === "LG" && lgVoteData) {
        const s = safeJoin(lgVoteData.Vote_Code, lgVoteData.Vote_Name);
        if (s) return s;
      }
      if (detectedUserType === "CG" && cgVoteData) {
        const s = safeJoin(cgVoteData.Vote_Code, cgVoteData.Vote_Name);
        if (s) return s;
      }
      if (detectedUserType === "SEPC" && sepVoteData) {
        const code = sepVoteData.Vote_Code || sepVoteData.Parastatal_Code;
        const name = sepVoteData.Vote_Name || sepVoteData.Parastatal_Name;
        const s = safeJoin(code, name);
        if (s) return s;
      }
      return VoteInfo || "Not assigned";
    }
    return VoteInfo || "Not assigned";
  };

  const getUserTypeDisplay = () => {
    const typeInfo = getUserTypeInfo(detectedUserType);
    if (!isCreateMode && creatorRoleDisplay) {
      return `${creatorRoleDisplay} (${typeInfo.name})`;
    }
    if (isSuperAdmin) return `Super Admin (${typeInfo.name})`;
    if (isBPEDMaster) return `BPED Master (${typeInfo.name})`;
    if (isAdmin) return `Administrator (${typeInfo.name})`;
    if (isBPED) return `BPED (${typeInfo.name})`;
    return `${userRoleDisplay} (${typeInfo.name})`;
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" backdrop="static" keyboard={false}>
        <Modal.Header closeButton={!loading}>
          <Modal.Title>
            {action === "Add" ? "Log New Feedback" : action === "View" ? "View Feedback" : action === "Update" ? "Update Feedback" : "Resolve Feedback"}
            {selectedFeedback && action !== "Add" && (
              <Badge variant={getUserTypeInfo(detectedUserType).variant} className="ml-2" style={{ fontSize: "0.7rem" }}>
                {getUserTypeInfo(detectedUserType).name}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit} autoComplete="off">
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            {/* ============ System Information (Vote / Role / Ticket) ============ */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    <strong>Vote:</strong>{" "}
                    {isCreateMode && isBPEDMaster ? "" : `(${detectedUserType})`}
                  </Form.Label>
                  <Form.Control type="text" value={getVoteDisplayInfo()} readOnly className="bg-light" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label><strong>Role:</strong></Form.Label>
                  <Form.Control type="text" value={getUserTypeDisplay()} readOnly className="bg-light" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label><strong>Ticket Number:</strong></Form.Label>
                  <Form.Control type="text" value={isCreateMode ? "Will be auto-generated on submit" : Ticket_No || "N/A"} readOnly className="bg-light" />
                  <Form.Text className="text-muted">
                    {isCreateMode ? `Format: ${getVoteCode()}-${getUsername()}-XX` : "Auto-generated ticket number"}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* ============ Logger's Contact Info (Edit / View / Resolve only) ============ */}
            {!isCreateMode && (
              <div className="border rounded p-3 mb-3 bg-white">
                <h6 className="mb-2"><i className="fas fa-address-card mr-2"></i> Logged By</h6>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label><strong>Name:</strong></Form.Label>
                      <Form.Control type="text" value={creatorDisplayName || "N/A"} readOnly className="bg-light" />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label><strong>Email:</strong></Form.Label>
                      <Form.Control type="text" value={creatorEmail || "N/A"} readOnly className="bg-light" />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label><strong>Phone:</strong></Form.Label>
                      <Form.Control type="text" value={creatorPhone || "N/A"} readOnly className="bg-light" />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}

            {/* ============ Super Admin Type & Vote Selection (create mode only) ============ */}
            {isSuperAdmin && isCreateMode && (
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><strong>Feedback Type:</strong></Form.Label>
                    <Form.Control as="select" value={selectedType} onChange={(e) => onTypeChange(e.target.value)} required>
                      <option value="CG">Central Government</option>
                      <option value="LG">Local Government</option>
                      <option value="SEPC">SEPC</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><strong>Select Vote/Parastatal:</strong></Form.Label>
                    <Form.Control as="select" value={selectedVote || ""} onChange={(e) => onVoteChange(e.target.value ? parseInt(e.target.value) : null)} required>
                      <option value="">Select {selectedType === "SEPC" ? "Parastatal" : "Vote"}...</option>
                      {availableVotes[selectedType]?.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>
            )}

            {/* ============ Feedback Details ============ */}
            <div className="border rounded p-3 mb-4">
              <h5 className="mb-3">Feedback Details</h5>

              {renderFieldWithLabelOnLeft(
                "Category: ",
                <Form.Control as="select" value={Feedback_Category} onChange={(e) => changeFeedback_Category(e.target.value)} disabled={loading || !isFieldEditable("Category")} required={isCreateMode}>
                  <option value="">Select category</option>
                  {Feedback_CategorysOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </Form.Control>,
                isCreateMode
              )}

              {renderFieldWithLabelOnLeft(
                "Rate of Occurrence: ",
                <Form.Control as="select" value={Rate_Of_Occurence} onChange={(e) => changeRate_Of_Occurence(e.target.value)} required={isCreateMode} disabled={loading || !isFieldEditable("Occurence_Rate")}>
                  <option value="">Select frequency</option>
                  {rate_Of_OccurenceOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </Form.Control>,
                isCreateMode
              )}

              {renderFieldWithLabelOnLeft(
                "Impact: ",
                <Form.Control as="select" value={Impact} onChange={(e) => changeImpact(e.target.value)} required={isCreateMode} disabled={loading || !isFieldEditable("Impact")}>
                  <option value="">Select impact level</option>
                  {ImpactOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </Form.Control>,
                isCreateMode,
                !isCreateMode && !isFieldEditable("Impact") ? "Impact can only be edited by Super Admin before resolution" : null
              )}

              {renderFieldWithLabelOnLeft(
                "Description: ",
                <Form.Control as="textarea" rows={5} placeholder="Please provide detailed description..." value={Feedback_Description} onChange={(e) => changeFeedback_Description(e.target.value)} required={isCreateMode} disabled={loading || !isFieldEditable("Description")} />,
                isCreateMode
              )}

              {!isResolveMode && (
                <Form.Group as={Row} className="mb-3 align-items-start">
                  <Form.Label column sm={3}>Image: <span className="text-muted">(Optional)</span></Form.Label>
                  <Col sm={9}>
                    {isCreateMode && (
                      <>
                        <Form.Control className="d-none" type="file" ref={imageRef} accept="image/*" onChange={handleImageUpload} disabled={loading || !isFieldEditable("Image_url")} />
                        <Button variant="outline-secondary" onClick={() => imageRef.current.click()} disabled={loading || !isFieldEditable("Image_url")}>
                          {imageFile ? "Update Image" : "Add Image"}
                        </Button>
                      </>
                    )}
                    {imageFile && (
                      <div className="d-inline-block ml-2">
                        <div onClick={() => handleImageClick(selectedImage || `${FeedbackImageUploadLink}/${imageFile}`)} style={{ cursor: "pointer" }}>
                          <Image src={selectedImage || `${FeedbackImageUploadLink}/${imageFile}`} thumbnail style={{ maxWidth: "200px", maxHeight: "150px" }} />
                        </div>
                        <small className="text-muted d-block">Click image to preview</small>
                        {isFieldEditable("Image_url") && (
                          <Button variant="link" size="sm" onClick={() => { changeImage(""); setSelectedImage(""); }} className="text-danger p-0">Remove image</Button>
                        )}
                      </div>
                    )}
                    {!imageFile && <p className="text-muted mb-0">No image attached</p>}
                  </Col>
                </Form.Group>
              )}
            </div>

            {/* ============ Review / Assignment Section ============ */}
            {showReviewerFields && (
              <div className="border rounded p-3 mb-4 bg-light">
                <h5 className="mb-3"><i className="fas fa-user-shield mr-2"></i> Review & Assignment Section</h5>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Status</strong></Form.Label>
                      <Form.Control as="select" value={Status || "Pending Review"} onChange={(e) => changeStatus(e.target.value)} disabled={loading || readOnly || !isFieldEditable("Status")}>
                        {statusOptions.map((option) => {
                          const isResolvedOption = option.value === "Resolved";
                          const isAssignedToCurrentUser = Assigned_To === userName;
                          const disableResolved = isResolvedOption && !isAssignedToCurrentUser;
                          return (
                            <option key={option.value} value={option.value} disabled={disableResolved}>
                              {option.label}
                            </option>
                          );
                        })}
                      </Form.Control>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Resolution Type</strong></Form.Label>
                      {isSuperAdmin && (isEditMode || isEditModeFromAction) ? (
                        <Form.Control as="select" value={Resolution_Type || ""} onChange={(e) => changeResolutionType(e.target.value)} disabled={loading || readOnly} required>
                          <option value="">Select resolution type</option>
                          {Resolution_TypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                        </Form.Control>
                      ) : (
                        <Form.Control type="text" value={Resolution_Type || "N/A"} readOnly className="bg-light" />
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Assigned To</strong></Form.Label>
                      {isSuperAdmin ? (
                        <Form.Control
                          as="select"
                          value={Assigned_To || ""}
                          onChange={(e) => changeAssignedTo(e.target.value)}
                          disabled={loading || readOnly || !isFieldEditable("Assigned_To") || usersLoading || (Status !== "Assigned" && Status !== "Escalated")}
                          required={isSuperAdmin && (Status === "Assigned" || Status === "Escalated")}
                        >
                          <option value="">Select a user to assign</option>
                          {assignableUsers.map((user) => <option key={user.value} value={user.value}>{user.label}</option>)}
                        </Form.Control>
                      ) : (
                        <Form.Control type="text" value={Assigned_To || "Not assigned"} readOnly className="bg-light" />
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {isResolveMode && isSuperAdmin && !Assigned_To && !readOnly && (
                  <Button variant="outline-primary" size="sm" onClick={() => changeAssignedTo(userName)} disabled={loading} className="mb-3">
                    Assign to Me
                  </Button>
                )}

                {(isResolveMode || Status === "Resolved" || isViewMode) && (
                  <Form.Group as={Row} className="mb-3 align-items-start">
                    <Form.Label column sm={2}>
                      Resolution Summary {(isResolveMode || (isEditModeFromAction && Status === "Resolved")) && <span className="text-danger">*</span>}
                    </Form.Label>
                    <Col sm={10}>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder={isViewMode ? (Resolution_Summary ? "Resolution details" : "No resolution summary provided") : "How was this issue resolved?"}
                        value={Resolution_Summary || ""}
                        onChange={(e) => changeResolutionSummary && changeResolutionSummary(e.target.value)}
                        required={isResolveMode || (isEditModeFromAction && Status === "Resolved")}
                        readOnly={isViewMode || readOnly || !isFieldEditable("Resolution_Summary")}
                        disabled={loading || readOnly || (!isFieldEditable("Resolution_Summary") && !isViewMode)}
                      />
                    </Col>
                  </Form.Group>
                )}
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={onHide} disabled={loading}>
              {readOnly ? "Close" : "Cancel"}
            </Button>
            {!readOnly && (
              <Button variant={isCreateMode ? "success" : isResolveMode ? "success" : "primary"} type="submit" disabled={loading}>
                {loading ? "Processing..." : isCreateMode ? "Submit Feedback" : isResolveMode ? "Resolve Feedback" : "Save Changes"}
              </Button>
            )}
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Image Preview Modal */}
      <Modal show={showImagePreview} onHide={() => setShowImagePreview(false)} size="lg" centered>
        <Modal.Header closeButton><Modal.Title>Image Preview</Modal.Title></Modal.Header>
        <Modal.Body className="text-center">
          {previewImageUrl && <Image src={previewImageUrl} fluid style={{ maxHeight: "70vh" }} alt="Preview" />}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowImagePreview(false)}>Close</Button></Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDelete} onHide={onHideDelete} backdrop="static" keyboard={false}>
        <Modal.Header closeButton={!loading}><Modal.Title>Delete Feedback</Modal.Title></Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <Alert.Heading>Warning!</Alert.Heading>
            <p>Are you sure you want to delete this feedback?</p>
            {Feedback_Description && (
              <div className="mt-2"><strong>Description:</strong><p className="mt-1 text-truncate">{Feedback_Description.substring(0, 100)}...</p></div>
            )}
            <p className="mb-0"><strong>Note:</strong> This action cannot be undone.</p>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHideDelete} disabled={loading}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={loading}>{loading ? "Deleting..." : "Delete Permanently"}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default FeedbackModal;